import type { Phrases } from '../common/Phrases.d';
import type { CommonSuccessPageParams } from '../common/CommonSuccessPageParams.d';
import {
	type AnonymousWhere,
	type When,
	ANONYMOUS_WHERE,
	WHEN,
} from './constants';

import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@hdir/ui/accordion';
import { Link } from '@hdir/ui/link';
import React, { type FC, useEffect, useRef } from 'react';
import { Span } from '../../../components/Element';
import { H3 } from '../../../components/heading/Heading';
import { Stack } from '../../../components/Flex';
import { Time } from '../../../components/Time';
import { FontAwesomeIconWrapper as Icon } from '../../../components/FontAwesomeIconWrapper';
import { sanitizeHtml } from '../../../shared/sanitizeHtml';
import {
	SuccessPageAdditionalInformation,
	SuccessPageCapsulePowderColors,
	SuccessPageColors,
	SuccessPageHeader1,
	SuccessPageImages,
	SuccessPageInfobox,
	SuccessPageIntoxicantDescription,
	SuccessPagePillShape,
	SuccessPageSubstance,
	SuccessPageSubstanceForm,
} from '../common/SuccessPage';


export interface SuccessPageParams extends CommonSuccessPageParams {
	whenWasTheDrugTaken: When;
	whenWasTheDrugTakenOther?: string;
	where: AnonymousWhere;
}

const translateWhere = ({
	phrases,
	where,
}: {
	phrases: Phrases;
	where?: string;
}): string => where === ANONYMOUS_WHERE.INTERNET
	? phrases['notificationForm.anonymous.whereField.value.internet']
	: where === ANONYMOUS_WHERE.OTHER
		? phrases['notificationForm.anonymous.whereField.value.other']
		: '';


const INTERNET_AND_OTHER: AnonymousWhere[] = [ANONYMOUS_WHERE.INTERNET, ANONYMOUS_WHERE.OTHER];

export const SuccessPageWhere = ({
	municipalityName,
	notFilledOut,
	phrases,
	where,
	whereOther,
}: {
	municipalityName?: string;
	notFilledOut: string;
	phrases: Phrases;
	where: AnonymousWhere;
	whereOther?: string;
}): React.JSX.Element => (
	<Stack className='gap-md'>
		<H3>{phrases['notificationForm.anonymous.whereField.label']}</H3>
		<p>{`${municipalityName || translateWhere({
			phrases,
			where,
		})}${
			whereOther ? `: ${whereOther}`
				: INTERNET_AND_OTHER.includes(where) ? ` ${notFilledOut}` : ''
		}`}</p>
	</Stack>
);

export const SuccessPage: FC<SuccessPageParams> = ({
	additionalInformation,
	capsulePowderColors,
	capsulePowderColorOther,
	colors,
	colorOther,
	imageDataUrls,
	intoxicantDescription,
	intoxicantOptions,
	municipalityName,
	phrases,
	pillShape,
	pillShapeOther,
	sitePageUrl,
	submissionDate,
	substance,
	substanceForm,
	substanceOther,
	translatedColors,
	where,
	whenWasTheDrugTaken,
	whenWasTheDrugTakenOther,
	whereOther,
}) => {
	const notFilledOut = phrases['notificationForm.term.notFilledOut'];

	const h1Ref = useRef<HTMLHeadingElement>();
	useEffect(() => {
		if (h1Ref.current) {
			h1Ref.current.scrollIntoView({ behavior: 'instant', block: 'start' });
			h1Ref.current.focus();
		}
	}, []);

	return (
		<Stack className='gap-lg' padding={{ block: 'lg' }}>
			<SuccessPageHeader1
				h1Ref={h1Ref}
				phrases={phrases}
			/>
			<SuccessPageInfobox phrases={phrases}/>
			<Accordion>
				<AccordionItem>
					<AccordionTrigger>
						<Span
							dangerouslySetInnerHTML={{
								__html: sanitizeHtml(
									phrases[
										'notificationForm.anonymous.successPage.accordionTrigger.html'
									],
								),
							}}
						/>
					</AccordionTrigger>
					<AccordionContent>
						<Stack className='gap-lg'>
							<Time dateTime={submissionDate}/>
							<SuccessPageSubstance
								intoxicantOptions={intoxicantOptions}
								notFilledOut={notFilledOut}
								phrases={phrases}
								substance={substance}
								substanceOther={substanceOther}
							/>
							<SuccessPageWhere
								municipalityName={municipalityName}
								notFilledOut={notFilledOut}
								phrases={phrases}
								where={where}
								whereOther={whereOther}
							/>
							<Stack className='gap-md'>
								<H3>{phrases['notificationForm.anonymous.whenField.label']}</H3>
								<p>
									{
										`${
											whenWasTheDrugTaken
												// I guess this is safe a long as types match i18n
												? phrases[`notificationForm.anonymous.whenField.value.${whenWasTheDrugTaken}`]
												: ''
										}${
											whenWasTheDrugTakenOther ? `: ${whenWasTheDrugTakenOther}`
												: whenWasTheDrugTaken === WHEN.OTHER ? ` ${notFilledOut}` : ''
										}`
									}
								</p>
							</Stack>
							<SuccessPageSubstanceForm phrases={phrases} substanceForm={substanceForm}/>
							<SuccessPagePillShape
								notFilledOut={notFilledOut}
								phrases={phrases}
								pillShape={pillShape}
								pillShapeOther={pillShapeOther}
							/>
							<SuccessPageColors
								colorOther={colorOther}
								colors={colors}
								notFilledOut={notFilledOut}
								phrases={phrases}
								substanceForm={substanceForm}
								translatedColors={translatedColors}
							/>
							<SuccessPageCapsulePowderColors
								capsulePowderColors={capsulePowderColors}
								capsulePowderColorOther={capsulePowderColorOther}
								notFilledOut={notFilledOut}
								phrases={phrases}
								translatedColors={translatedColors}
							/>
							<SuccessPageIntoxicantDescription
								intoxicantDescription={intoxicantDescription}
								notFilledOut={notFilledOut}
								phrases={phrases}
								substanceForm={substanceForm}
							/>
							<SuccessPageImages
								imageDataUrls={imageDataUrls}
								phrases={phrases}
							/>
							<SuccessPageAdditionalInformation
								additionalInformation={additionalInformation}
								notFilledOut={notFilledOut}
								phrases={phrases}
							/>
						</Stack>
					</AccordionContent>
				</AccordionItem>
			</Accordion>
			<Link variant="button-secondary" href={sitePageUrl}>Gå til forsiden <Icon icon={faArrowRight}/></Link>
		</Stack>
	);
};
